import type {
  CampaignTemplateQueryResponse,
  CampaignTemplateQueryVariables,
} from '~/api/graphql/queries/campaignTemplate.generated';
import type { Campaign, Variant } from '~/modules/campaign/types';
import type { CampaignType } from '~/types/graphql';

import {
  buildProductVariantTasks,
  buildVariantTasks,
  buildVariantUpdateInput,
  detectCampaignSettingsChanged,
  determinePriceAction,
  determineUrlAction,
  mergeUpdatedVariants,
  toSetVariantProductVariantInput,
} from '../helpers';
import { useVariantProductVariantsBulkSetMutation } from '~/api/graphql/mutations/variantProductVariantsBulkSet.generated';
import { useVariantTemplateUpdateMutation } from '~/api/graphql/mutations/variantTemplateUpdate.generated';
import { useVariantPriceCreateMutation } from '~/api/graphql/mutations/variantPriceCreate.generated';
import { useVariantPriceDeleteMutation } from '~/api/graphql/mutations/variantPriceDelete.generated';
import { useVariantPriceUpdateMutation } from '~/api/graphql/mutations/variantPriceUpdate.generated';
import { useVariantUrlCreateMutation } from '~/api/graphql/mutations/variantUrlCreate.generated';
import { useVariantUrlDeleteMutation } from '~/api/graphql/mutations/variantUrlDelete.generated';
import { useVariantUrlUpdateMutation } from '~/api/graphql/mutations/variantUrlUpdate.generated';
import { useCampaignUpdateMutation } from '~/api/graphql/mutations/campaignUpdate.generated';
import { CampaignTemplateDocument } from '~/api/graphql/queries/campaignTemplate.generated';
import { useVariantUpdateMutation } from '~/api/graphql/mutations/variantUpdate.generated';
import { captureSentryError } from '~/exceptions/sentry';
import { useShopifyThemeInfo } from '~/modules/shopify';
import { appFetcher } from '~/api/utils/gatewayClient';
import { CAMPAIGN_TYPE } from '~/constants/campaign';
import { useQueryThemePages } from '~/modules/apps';
import { useCampaignStore } from '../stores';
import { getShopifyConfig } from '~/config';
import { mapThemePageInfo } from '~/utils';

interface IUpdateCampaignOptions {
  storedCampaign?: Campaign;
  isCGTemplate?: boolean;
}

interface IUpdateCampaignVariant {
  updateVariantDetails: (variant: Variant) => Promise<Variant | undefined>;
  updateCampaign: (campaign: Campaign, options?: IUpdateCampaignOptions) => Promise<Campaign>;
}

export const useUpdateCampaignVariant = (): IUpdateCampaignVariant => {
  const { themePages } = useQueryThemePages();
  const { shopifyThemeId } = useShopifyThemeInfo();
  const getCampaignById = useCampaignStore((state) => state.getCampaignById);

  const updateCampaignMutation = useCampaignUpdateMutation();
  const updateVariantMutation = useVariantUpdateMutation();
  const updateVariantTemplateMutation = useVariantTemplateUpdateMutation();
  const variantPriceCreateMutation = useVariantPriceCreateMutation();
  const variantPriceUpdateMutation = useVariantPriceUpdateMutation();
  const variantPriceDeleteMutation = useVariantPriceDeleteMutation();
  const variantUrlCreateMutation = useVariantUrlCreateMutation();
  const variantUrlUpdateMutation = useVariantUrlUpdateMutation();
  const variantUrlDeleteMutation = useVariantUrlDeleteMutation();
  const variantProductVariantsBulkSetMutation = useVariantProductVariantsBulkSetMutation();

  const mutateCampaign = async (campaign: Campaign, isCGTemplate?: boolean): Promise<Campaign> => {
    const campaignInput = {
      type: campaign.type,
      priority: campaign.priority,
      storeDomain: campaign.storeDomain,
      targetChannels: campaign.targetChannels,
      targetCountries: campaign.targetCountries,
      targetDevices: campaign.targetDevices,
      targetVisitorTypes: campaign.targetVisitorTypes,
      conditions: campaign.conditions,
    };

    const campaignInGroupInput = {
      ...(campaign.conditions?.targetPageIds
        ? { conditions: { targetPageIds: campaign.conditions.targetPageIds } }
        : {}),
    };

    const { campaignUpdate } = await updateCampaignMutation.mutateAsync({
      id: campaign.id,
      input: isCGTemplate ? campaignInGroupInput : campaignInput,
    });
    return {
      ...campaignUpdate,
      id: campaign.id,
      name: campaign.name,
    };
  };

  const updateVariant = async (data: {
    variant?: Variant;
    campaignType?: CampaignType | null;
  }): Promise<Variant | undefined> => {
    const { variant, campaignType } = data;
    if (!variant?.id) return;

    const { variantUpdate } = await updateVariantMutation.mutateAsync({
      id: variant.id,
      input: buildVariantUpdateInput({ variant, campaignType }),
    });

    return {
      ...variant,
      ...variantUpdate,
    };
  };

  const updateTemplate = async (variant?: Variant) => {
    if (!variant?.template?.id) return;

    const themePageInfo = mapThemePageInfo(variant.templateSuffix, themePages);
    const { variantTemplateUpdate } = await updateVariantTemplateMutation.mutateAsync({
      id: variant.template.id,
      input: {
        redirectURL: variant.template.redirectURL,
        themeID: variant.template.themeID || shopifyThemeId,
        ...(themePageInfo?.label ? { title: themePageInfo.label } : {}),
        ...(themePageInfo?.thumbnail ? { thumbnailURL: themePageInfo.thumbnail } : {}),
      },
    });

    return variantTemplateUpdate;
  };

  const updateVariantAndTemplate = async (variant?: Variant): Promise<Variant | undefined> => {
    if (!variant?.id || !variant.template) return;

    const [updatedVariant, updatedTemplate] = await Promise.all([
      updateVariant({ variant }),
      updateTemplate(variant),
    ]);

    if (!updatedVariant) return;

    return {
      ...updatedVariant,
      template: updatedTemplate ?? updatedVariant.template,
    };
  };

  // A URL test picks any storefront page and resolves that page's URL itself, so its own redirectURL
  // wins. Only a price test — always a product — leaves it unset and has one composed from the handle.
  const buildPriceRedirectURL = (price: NonNullable<Variant['price']>): string => {
    if (price.redirectURL) return price.redirectURL;

    const shopifyDomain = getShopifyConfig()?.shopifyDomain;

    return price.handle && shopifyDomain ? `https://${shopifyDomain}/products/${price.handle}` : '';
  };

  const updatePrice = async (
    variant: Variant,
    storedVariant?: Variant,
  ): Promise<Partial<Variant> | undefined> => {
    const priceAction = determinePriceAction(variant, storedVariant);

    if (priceAction === 'create' && variant.price) {
      const { variantPriceCreate } = await variantPriceCreateMutation.mutateAsync({
        input: {
          variantID: variant.id,
          baseID: variant.price.baseID,
          handle: variant.price.handle,
          title: variant.price.title,
          status: variant.price.status,
          redirectURL: buildPriceRedirectURL(variant.price),
        },
      });
      return { price: variantPriceCreate };
    }

    if (priceAction === 'update' && variant.price && storedVariant?.price?.id) {
      const { variantPriceUpdate } = await variantPriceUpdateMutation.mutateAsync({
        id: storedVariant.price.id,
        input: {
          baseID: variant.price.baseID,
          handle: variant.price.handle,
          title: variant.price.title,
          status: variant.price.status,
          redirectURL: buildPriceRedirectURL(variant.price),
        },
      });
      return { price: variantPriceUpdate };
    }

    if (priceAction === 'delete' && storedVariant?.price?.id) {
      await variantPriceDeleteMutation.mutateAsync({ id: storedVariant.price.id });
      return { price: undefined };
    }

    return undefined;
  };

  // A URL test resolves the destination itself when the page is picked, so the record is saved with
  // exactly the address the pick produced -- nothing is composed from the handle here.
  const updateUrl = async (
    variant: Variant,
    storedVariant?: Variant,
  ): Promise<Partial<Variant> | undefined> => {
    const urlAction = determineUrlAction(variant, storedVariant);

    if (urlAction === 'create' && variant.url) {
      const { variantURLCreate } = await variantUrlCreateMutation.mutateAsync({
        input: {
          variantID: variant.id,
          baseID: variant.url.baseID,
          handle: variant.url.handle,
          title: variant.url.title,
          redirectURL: variant.url.redirectURL,
          resourceType: variant.url.resourceType,
        },
      });
      return { url: variantURLCreate };
    }

    if (urlAction === 'update' && variant.url && storedVariant?.url?.id) {
      const { variantURLUpdate } = await variantUrlUpdateMutation.mutateAsync({
        id: storedVariant.url.id,
        input: {
          baseID: variant.url.baseID,
          handle: variant.url.handle,
          title: variant.url.title,
          redirectURL: variant.url.redirectURL,
          resourceType: variant.url.resourceType,
        },
      });
      return { url: variantURLUpdate };
    }

    if (urlAction === 'delete' && storedVariant?.url?.id) {
      await variantUrlDeleteMutation.mutateAsync({ id: storedVariant.url.id });
      return { url: undefined };
    }

    return undefined;
  };

  // The store is a per-tab snapshot, so it can still name a price record another tab removed --
  // updating that id fails with `variant_price not found`. Not via queryClient: every observer of
  // that key would re-hydrate the screen mid-save.
  const fetchLatestCampaign = async (campaignId: string): Promise<Campaign | undefined> => {
    try {
      const data = await appFetcher<CampaignTemplateQueryResponse, CampaignTemplateQueryVariables>(
        CampaignTemplateDocument,
        { id: campaignId },
      )();

      return (data.campaign as Campaign | undefined) ?? getCampaignById(campaignId);
    } catch (error) {
      captureSentryError(error as Error, {
        source: 'useUpdateCampaignVariant.fetchLatestCampaign',
        level: 'warning',
        tags: { experiment: 'Experiment' },
      });

      return getCampaignById(campaignId);
    }
  };

  /** A price test's per-variant prices — saved to GemCommerce only, they reach the duplicate on start. */
  const updateProductVariants = async (variant: Variant): Promise<Partial<Variant>> => {
    const { variantProductVariantsBulkSet } =
      await variantProductVariantsBulkSetMutation.mutateAsync({
        variantID: variant.id,
        input: toSetVariantProductVariantInput(variant.productVariants),
      });

    return { productVariants: variantProductVariantsBulkSet };
  };

  // Price and URL only -- no other campaign type carries a record id that goes stale like this.
  const resolveStoredCampaign = async (campaign: Campaign): Promise<Campaign | undefined> => {
    if (campaign.type === CAMPAIGN_TYPE.PRICE || campaign.type === CAMPAIGN_TYPE.URL) {
      return fetchLatestCampaign(campaign.id);
    }

    return getCampaignById(campaign.id);
  };

  const updateCampaign = async (
    campaign: Campaign,
    options?: IUpdateCampaignOptions,
  ): Promise<Campaign> => {
    if (!campaign?.variants) {
      throw new Error('Campaign or variants not found');
    }

    const { storedCampaign: externalStoredCampaign, isCGTemplate } = options || {};

    // Use external storedCampaign if provided (for campaign group)
    const storedCampaign = externalStoredCampaign ?? (await resolveStoredCampaign(campaign));

    const hasCampaignSettingsChanged = detectCampaignSettingsChanged(campaign, storedCampaign);

    const variantTasks = buildVariantTasks({
      variants: campaign.variants,
      storedVariants: storedCampaign?.variants,
      updateVariantAndTemplate,
      updateVariant: (variant) => updateVariant({ variant, campaignType: campaign.type }),
      updatePrice,
      updateUrl,
    });

    const isControlTemplateChanged = variantTasks.some((t) => t.isOrigin && t.hasTemplateChange);

    const campaignPromise =
      hasCampaignSettingsChanged || isControlTemplateChanged
        ? mutateCampaign(campaign, isCGTemplate)
        : null;
    const variantPromises = variantTasks.map((t) => t.promise);

    const [campaignResult, variantResults] = await Promise.all([
      campaignPromise,
      Promise.all(variantPromises),
    ]);
    const updatedVariants = mergeUpdatedVariants(campaign.variants, variantTasks, variantResults);

    // Diffed off the caller's `campaign` rather than `updatedVariants`: a variant result above may carry
    // the server's old `productVariants` back and hide the edit.
    const productVariantTasks = buildProductVariantTasks({
      variants: campaign.variants,
      storedVariants: storedCampaign?.variants,
      updateProductVariants,
    });
    const productVariantResults = await Promise.all(
      productVariantTasks.map((t) => t.promise),
    ).catch((error) => {
      captureSentryError(error, {
        source: 'useUpdateCampaignVariant.updateProductVariants',
        tags: { experiment: 'Experiment' },
        extra: { campaignId: campaign.id },
      });
      throw error;
    });

    return {
      ...(campaignResult ? { ...campaign, ...campaignResult } : campaign),
      variants: mergeUpdatedVariants(updatedVariants, productVariantTasks, productVariantResults),
    };
  };

  return {
    updateVariantDetails: updateVariantAndTemplate,
    updateCampaign,
  };
};
